import components from '@scripts/components.json';
import * as consts from '@utilities/consts';

type ModuleName = string & keyof typeof components;

declare global {
  interface Window {
    loadComponent: (
      name: ModuleName,
      opts?: { base?: string; manifestPath?: string; refresh?: boolean },
    ) => Promise<void>;
  }
}

(() => {
  const DEFAULT_BASE = consts.ASSET_BASEURL;
  const DEFAULT_MANIFEST = '/manifest.json';
  const MANIFEST_OVERRIDE_KEY = 'componentManifestUrl';

  type Manifest = { version: string; modules: Record<ModuleName, string> };

  let cached: Manifest | undefined;
  let inflightManifest: Promise<Manifest> | undefined;
  let cachedManifestUrl: string | undefined;
  const loaded = new Set<string>();

  function abs(base: string, p: string) {
    return /^https?:\/\//i.test(p)
      ? p
      : `${base.replace(/\/$/, '')}${p.startsWith('/') ? '' : '/'}${p}`;
  }

  function getManifestUrl(opts?: { base?: string; manifestPath?: string }) {
    try {
      const override = window.localStorage.getItem(MANIFEST_OVERRIDE_KEY)?.trim();
      if (override) return override;
    } catch {}
    const base = opts?.base || DEFAULT_BASE;
    const manifestPath = opts?.manifestPath || DEFAULT_MANIFEST;
    return abs(base, manifestPath);
  }

  function getManifest(manifestUrl: string, refresh: boolean) {
    if (cached && !refresh && cachedManifestUrl === manifestUrl) {
      return Promise.resolve(cached);
    }
    if (inflightManifest && !refresh && cachedManifestUrl === manifestUrl) {
      return inflightManifest;
    }
    const url =
      manifestUrl + (refresh ? `${manifestUrl.includes('?') ? '&' : '?'}_=${Date.now()}` : '');
    inflightManifest = fetch(url, { cache: refresh ? 'no-store' : 'no-cache' })
      .then((r) => {
        if (!r.ok) throw new Error(`Manifest fetch failed: ${r.status}`);
        return r.json();
      })
      .then((m: Manifest) => {
        cached = m;
        cachedManifestUrl = manifestUrl;
        return m;
      })
      .finally(() => {
        inflightManifest = undefined;
      });
    return inflightManifest;
  }

  async function loadComponent(
    name: ModuleName,
    opts?: { base?: string; manifestPath?: string; refresh?: boolean },
  ) {
    const manifestUrl = getManifestUrl(opts);
    const m = await getManifest(manifestUrl, !!opts?.refresh);
    const rel = m.modules[name];
    if (!rel) throw new Error(`Unknown module: ${name}`);
    const base = opts?.base || new URL(manifestUrl).origin;
    const url = abs(base, rel);
    if (loaded.has(url)) return;
    await new Promise<void>((resolve, reject) => {
      const s = document.createElement('script');
      s.src = url;
      s.defer = true;
      s.onload = () => {
        loaded.add(url);
        resolve();
      };
      s.onerror = () => reject(new URIError(`Failed to load ${url}`));
      document.head.appendChild(s);
    });
  }

  window.loadComponent = loadComponent;
})();
